import React, { useState, useEffect } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { Icon } from './Icon';
import { Tooltip } from './Tooltip';
import styles from './FloatButton.module.css';

const floatButton = cva(styles.base, {
  variants: {
    type: {
      default: styles.default,
      primary: styles.primary,
    },
    shape: {
      circle: styles.circle,
      square: styles.square,
    },
  },
  defaultVariants: { type: 'default', shape: 'circle' },
});

type FloatButtonType = 'default' | 'primary';
type FloatButtonShape = 'circle' | 'square';

type ButtonMotionSafeProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'>;

interface FloatButtonProps extends Omit<ButtonMotionSafeProps, 'type'>, VariantProps<typeof floatButton> {
  /** Button icon */
  icon?: React.ReactNode;
  /** Visual type */
  type?: FloatButtonType;
  /** Shape */
  shape?: FloatButtonShape;
  /** Tooltip text */
  tooltip?: string;
  /** Description text below icon */
  description?: React.ReactNode;
}

/**
 * FloatButton — floating action button.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | icon | ReactNode | — | Button icon |
 * | type | 'default' \| 'primary' | 'default' | Visual style |
 * | shape | 'circle' \| 'square' | 'circle' | Shape |
 * | tooltip | string | — | Hover tooltip |
 * | description | ReactNode | — | Text below icon |
 *
 * Sub-components: `FloatButton.Group`, `FloatButton.BackTop`
 *
 * @example
 * <FloatButton icon={<Plus size={20} />} type="primary" />
 * <FloatButton.BackTop />
 */
export function FloatButton({
  icon,
  type = 'default',
  shape = 'circle',
  tooltip,
  description,
  style,
  className,
  ...props
}: FloatButtonProps) {
  const safeProps = props;
  const btn = (
    <motion.button
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: duration.slow / 1000 }}
      className={clsx(
        floatButton({ type, shape, className }),
        description && styles.withDescription,
      )}
      style={style}
      {...safeProps}
    >
      {icon && <span className={styles.icon}>{icon}</span>}
      {description && <span className={styles.description}>{description}</span>}
    </motion.button>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement="left">
        {btn}
      </Tooltip>
    );
  }

  return btn;
}

// ── FloatButton.Group ────────────────────────────────────────────────────────

interface FloatButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

function FloatButtonGroup({ children, style, className, ...props }: FloatButtonGroupProps) {
  return (
    <div
      className={clsx(styles.group, className)}
      style={style}
      {...props}
    >
      {children}
    </div>
  );
}

// ── FloatButton.BackTop ──────────────────────────────────────────────────────

interface BackTopProps extends Omit<FloatButtonProps, 'icon' | 'onClick'> {
  /** Scroll threshold before showing (px) */
  visibilityHeight?: number;
  /** Scroll target (defaults to window) */
  target?: () => HTMLElement;
}

function BackTop({ visibilityHeight = 400, target, ...props }: BackTopProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = target?.() ?? window;
    const handleScroll = () => {
      const scrollTop = el === window ? document.documentElement.scrollTop : (el as HTMLElement).scrollTop;
      setVisible(scrollTop > visibilityHeight);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => el.removeEventListener('scroll', handleScroll);
  }, [visibilityHeight, target]);

  return (
    <AnimatePresence>
      {visible && (
    <FloatButton
      icon={<Icon name="arrow_upward" size={20} />}
      tooltip="Back to top"
      onClick={() => {
        const el = target?.() ?? window;
        if (el === window) window.scrollTo({ top: 0, behavior: 'smooth' });
        else (el as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' });
      }}
      {...props}
    />
      )}
    </AnimatePresence>
  );
}

// ── Attach sub-components ────────────────────────────────────────────────────

FloatButton.Group = FloatButtonGroup;
FloatButton.BackTop = BackTop;
